import React, {
  ReactElement,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import {createPortal} from "react-dom";

import {
  css,
  generateKeyId,
  hideWindowScroll,
  inertContent,
  restoreWindowScroll,
} from "/src/utils/utils";
import {SimpleFormProps} from "/src/types";

import Image, {ImageObject} from "../../image/Image";
import CTAButton, {CTA} from "../../buttons/cta-button/CTAButton";
import SimpleFormWrapper from "../../forms/simple-form-wrapper/SimpleFormWrapper";
import Video, {VideoType} from "../../videos/video/Video";
import MarkdownText from "../../markdown/markdown-text/MarkdownText";
import ModuleBody from "../../layouts/module-body/ModuleBody";
import Icon from "../../icon/Icon";
import Button from "../../buttons/button/Button";
import SimpleFormField from "../../forms/simple-form-field/SimpleFormField";
import {AddressObjectType} from "/src/types";
import {sessionStorageEnabled} from "/src/utils/path-logging";
import {SESSION_STORAGE_NAME} from "../../forms/simple-form/SimpleForm";

import translate from "/src/utils/translate";
import CloseIcon from "/src/svg/close.svg";
import useHasWindow from "/src/hooks/useHasWindow";

import * as styles from "./stacked-hero.module.scss";

interface StackedHeroProps {
  bgImage?: ImageObject;
  bgVideo?: string;
  copy: string;
  ctas: Array<CTA>;
  eyebrow: string;
  eyebrowIcon: ImageObject;
  form?: SimpleFormProps;
  headline: string;
  largeHeadline?: boolean;
  showFormInDrawer?: boolean;
  signUpServicesAbTestingAlternative?: boolean;
  stacked?: boolean;
  theme?: "light" | "dark" | "grey";
  titleWithLineBreaks?: string;
  useTitleWithLineBreaks?: boolean;
  videoBehavior?: VideoType;
  videoControls?: boolean;
  wistiaBgVideo?: string;
}

interface ABTestingSignupServicesProps {
  form: SimpleFormProps;
  onSubmit: () => void;
}

/**
 * Calculate the top position of the drawer. Factors to accommodate:
 * - Height of header (main nav + announcement bar)
 * - Any scrolling of the page.
 */
const calculateHeaderOffset = () => {
  const $header =
    typeof document !== "undefined" ? document.getElementById("header") : null;

  if ($header == null || typeof window === "undefined") {
    return 0;
  }

  const offset = Math.max(0, $header.offsetHeight - window.scrollY);

  return offset;
};

function ABTestingSignupServices({
  form,
  onSubmit,
}: ABTestingSignupServicesProps) {
  const TARGET_FIELD_NAME = "Signup_Services";
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const signupServicesFields =
    form.fieldItems != null
      ? form.fieldItems
          .filter(fieldItem => fieldItem?.field_name === TARGET_FIELD_NAME)
          .pop()
      : null;

  if (signupServicesFields == null) {
    return null;
  }

  function setSessionStorage(data: string) {
    if (sessionStorageEnabled != undefined) {
      window.sessionStorage.setItem(
        SESSION_STORAGE_NAME,
        JSON.stringify({[TARGET_FIELD_NAME]: data})
      );
    }
  }

  function submitHandler(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();

    const $form = e.target as HTMLFormElement;
    const formData = new FormData($form);
    const dataIsEmpty = Array.from(formData.keys()).length === 0;

    if (dataIsEmpty) {
      return;
    }

    const formDataValues = Array.from(formData.values()).join(",");

    setSessionStorage(formDataValues);

    if (onSubmit) {
      onSubmit();
    }
  }

  function onChange(value: string | AddressObjectType) {
    if (typeof value !== "string") {
      return;
    }

    setSelectedOptions(value.split(","));
  }

  return (
    <form className={css(styles.abTest)} onSubmit={submitHandler}>
      <SimpleFormField
        field={signupServicesFields}
        onChange={onChange}
        value={selectedOptions.join(",")}
        valueForAddressField={undefined}
        transparentBg
        status=""
        readOnly={false}
        isEU={false}
        isEMEA={false}
        disabled={false}
        darkMode={false}
      />

      <button type="submit" className={css(styles.abTest__submit)}>
        {translate("Submit")}
      </button>
    </form>
  );
}

const FORM_DRAWER_ID = "stacked-hero-form-drawer";

const StackedHero = (props: StackedHeroProps): ReactElement => {
  const {
    bgImage,
    bgVideo,
    copy,
    ctas,
    eyebrow,
    eyebrowIcon,
    form,
    headline,
    largeHeadline,
    showFormInDrawer,
    signUpServicesAbTestingAlternative = false,
    stacked = true,
    theme,
    titleWithLineBreaks,
    useTitleWithLineBreaks,
    videoBehavior,
    videoControls,
    wistiaBgVideo,
  } = props;

  // this is to fix rehydration issue on static build
  const hasWindow = useHasWindow();
  const [showDrawer, setShowDrawer] = useState(false);
  const [mountDrawer, setMountDrawer] = useState(false);
  const $portal = useRef<HTMLElement | null>(null);
  const $drawer = useRef<HTMLDivElement | null>(null);

  const isDarkMode = theme === "dark";

  useEffect(() => {
    if (mountDrawer === true) {
      setTimeout(() => {
        setShowDrawer(true);
      }, 10);
    }

    if (mountDrawer === false) {
      restoreWindowScroll();
      inertContent(false);
    }
  }, [mountDrawer]);

  useEffect(() => {
    // on mount get portal element
    $portal.current = document.getElementById("portal-stacked-hero-drawer");

    // close the drawer if the user clicks outside of it
    function windowClickHandler(e: MouseEvent): void {
      if ((e.target as HTMLElement).closest(`#${FORM_DRAWER_ID}`)) {
        return;
      }

      setShowDrawer(false);
    }

    if (showDrawer === true) {
      window.addEventListener("click", windowClickHandler);
      hideWindowScroll();
      inertContent(true);
    }

    if (showDrawer === false && $drawer.current != null) {
      $drawer.current.addEventListener(
        "transitionend",
        () => {
          setMountDrawer(false);
        },
        {once: true}
      );
    }

    return () => {
      window.removeEventListener("click", windowClickHandler);
    };
  }, [showDrawer]);

  const toggleDrawer = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setMountDrawer(true);
  };

  const constructVideoComponent = () => {
    if (bgVideo == null && wistiaBgVideo == null) {
      return null;
    }

    if (wistiaBgVideo != null) {
      return (
        <div className={css(styles.stackedHero__media__videoWrapper)}>
          <Video
            videoConfig={{
              wistia: {
                options: {
                  fitStrategy: "contain",
                },
              },
            }}
            videoTitle={headline!}
            type={videoBehavior}
            videoSource={wistiaBgVideo}
            videoClasses={styles.stackedHero__media__video}
            showControl={videoControls === true}
          />
        </div>
      );
    }

    return (
      <div className={css(styles.stackedHero__media__videoWrapper)}>
        <Video
          videoTitle={headline!}
          type={videoBehavior}
          videoSource={bgVideo!}
          videoClasses={styles.stackedHero__media__video}
          showControl={videoControls === true}
        />
      </div>
    );
  };

  const shouldShowDrawer =
    form != null &&
    form.__typename === "ContentfulSimpleForm" &&
    showFormInDrawer === true &&
    hasWindow === true &&
    mountDrawer === true;

  const shouldShowABTest =
    form != null &&
    hasWindow === true &&
    showFormInDrawer === true &&
    signUpServicesAbTestingAlternative === true;

  return (
    <div
      className={css(
        styles.stackedHero,
        isDarkMode ? styles.stackedHeroDark : "",
        theme === "grey" ? styles.stackedHeroGrey : "",
        largeHeadline ? styles.stackedHeroLargeHeadline : ""
      )}
    >
      <ModuleBody
        classes={css(
          styles.stackedHero__body,
          stacked ? styles.stackedHero__bodyStacked : ""
        )}
      >
        {eyebrow && (
          <div className={styles.stackedHero__eyebrowWrapper}>
            {eyebrowIcon && (
              <Image
                image={eyebrowIcon}
                classes={styles.stackedHero__eyebrowIcon}
              />
            )}
          </div>
        )}

        <div className={styles.stackedHero__media}>
          {!bgVideo && !wistiaBgVideo && bgImage && (
            <Image
              image={bgImage}
              objectFit="contain"
              classes={styles.stackedHero__media__img}
            />
          )}

          {constructVideoComponent()}
        </div>

        <div className={styles.stackedHero__left}>
          {useTitleWithLineBreaks && titleWithLineBreaks != null && (
            <h1
              className={css(
                "h1",
                largeHeadline ? "h1--large" : "",
                styles.stackedHero__left__headline
              )}
            >
              <span
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html:
                    titleWithLineBreaks != null
                      ? titleWithLineBreaks.replace(/(\n|\r)/g, "<br/>")
                      : "",
                }}
              />
            </h1>
          )}

          {!useTitleWithLineBreaks && headline != null && (
            <h1
              className={css(
                "h1",
                largeHeadline ? "h1--large" : "",
                styles.stackedHero__left__headline
              )}
            >
              {headline}
            </h1>
          )}

          {copy && (
            <MarkdownText
              text={copy}
              classes={css(styles.stackedHero__left__copy)}
            />
          )}

          {shouldShowABTest ? (
            <ABTestingSignupServices
              form={form}
              onSubmit={() => setMountDrawer(true)}
            />
          ) : (
            <>
              {/* we will also assume that the first CTA would be primary, subsequent cta should be secondary */}
              {ctas && (
                <ul className={styles.stackedHero__left__ctas}>
                  {ctas.map((cta, i) => {
                    return (
                      <li
                        key={generateKeyId(`${cta.label.toLowerCase()}-${i}`)}
                        className={styles.stackedHero__left__cta}
                      >
                        {/*
                         * We're working on an assumption that:
                         * 1) if the form is going to live inside a drawer, the
                         *    first CTA button will be overriden to behave as a
                         *    toggle for the drawer.
                         * 2) whoever wants to use the form in a drawer will only
                         *    have 1 CTA button attached.
                         */}
                        {showFormInDrawer ? (
                          <Button
                            classes={styles.stackedHero__left__ctaBtn}
                            onClick={toggleDrawer}
                            kind={i === 0 ? "primary" : "secondary"}
                            aria-controls={FORM_DRAWER_ID}
                          >
                            {cta.label}
                          </Button>
                        ) : (
                          <CTAButton
                            classes={styles.stackedHero__left__ctaBtn}
                            darkMode={isDarkMode}
                            cta={cta}
                            kind={i === 0 ? "primary" : "secondary"}
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}

          {shouldShowDrawer &&
            $portal.current &&
            createPortal(
              <div
                ref={$drawer}
                id={FORM_DRAWER_ID}
                aria-hidden={!showDrawer}
                role="region"
                style={{top: calculateHeaderOffset()}}
                className={css(
                  styles.stackedHero__drawer,
                  showDrawer ? styles.stackedHero__drawerVisible : ""
                )}
              >
                {/* aria alert, for accessibility support */}
                <div
                  role="alert"
                  id="search-drawer-alert"
                  aria-atomic="true"
                  className={styles.stackedHero__drawer__alert}
                  aria-live="assertive"
                >
                  {translate(
                    showDrawer ? "Drawer form showed" : "Drawer form closed"
                  )}
                </div>
                <SimpleFormWrapper
                  {...form}
                  darkMode
                  transparentTitle
                  classes={css(
                    form.layout === "compact"
                      ? styles.stackedHero__left__form
                      : ""
                  )}
                />

                <button
                  type="button"
                  className={css(styles.stackedHero__drawer__closeBtn)}
                  onClick={() => setShowDrawer(false)}
                  aria-label={translate("Hide drawer form")}
                >
                  <Icon
                    svg={CloseIcon}
                    role="presentation"
                    classes={styles.stackedHero__drawer__closeIcon}
                  />
                </button>
              </div>,
              $portal.current
            )}

          {/* eslint-disable no-underscore-dangle */}
          {form != null &&
            !showFormInDrawer &&
            form.__typename === "ContentfulSimpleForm" &&
            form.layout === "compact" && (
              <SimpleFormWrapper
                {...form}
                darkMode
                classes={styles.stackedHero__left__form}
              />
            )}
        </div>
      </ModuleBody>
    </div>
  );
};

export default StackedHero;
